// Boards: DeckAnswer, DeckAnswerNew, DeckAnswerEditCard, DeckAnswerFull, DeckQuestion, DeckQuestionNew,
// DeckQuestionTwoBlanks, DeckQuestionFull, DeleteDeckConfirm; SharedView (D5) and SharedEdit (D6) for a deck shared
// with me, with the question rows and field for a question deck (note n16). The owner's "Share" opens ShareSheet.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { currentLanguage, t } from '../../i18n';
import { Bdi, screenDir, textDir } from '../../i18n/direction';
import { cardCount } from './decks';
import QuestionField, { MAX_BLANKS, partsLength } from './QuestionField';
import ShareSheet from './ShareSheet';
import { BackIcon, GeneralError, LevelTag, PersonIcon, PlusIcon, isBlank, onTap } from './parts';
import LanguageMenu from './LanguageMenu';

const PencilIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16z"></path></svg>
);
const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"></path></svg>
);

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="9" cy="8" r="3.5"></circle><path d="M2.5 20c0-3.5 3-5.5 6.5-5.5s6.5 2 6.5 5.5"></path><path d="M19 8v6M16 11h6"></path></svg>
);

// 3 answers is a game rule of its own: each player gets 2 more cards.
const answersMeta = n => (n === 3 ? t('deck.answersThree') : t('deck.answers', { count: n }));

export default function DeckEditor({ deckId, tab, sharing, onShare, onCloseShare, onBack, onGone, onAuthFailure }) {
  const [deck, setDeck] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [name, setName] = useState('');
  const [text, setText] = useState(''); // answer card field
  const [parts, setParts] = useState(['']); // question card field: text between blanks
  const [answers, setAnswers] = useState(1);
  const [editing, setEditing] = useState(null); // id of the card in the field
  const [fieldKey, setFieldKey] = useState(0); // remounts the question field to load other text
  const [error, setError] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const busy = useRef({}); // one request at a time per action
  const pending = useRef(new Set()); // writes in flight; Back waits for them
  const live = useRef(true);
  const question = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`decks/${encodeURIComponent(deckId)}`);
      if (!live.current) return;
      setDeck(res.data);
      setName(n => (n === '' ? res.data.name : n));
    } catch (e) {
      if (!live.current || await onAuthFailure(e)) return;
      if (e.response?.status === 404) onGone();
      else {
        setLoadFailed(true);
        setError(true);
      }
    }
  };

  useEffect(() => {
    live.current = true;
    load();
    return () => { live.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckId]);

  if (deck === null && !loadFailed) return null; // loading

  // A failed first load still draws the screen, so the error has its place under the card field.
  const shown = deck ?? { type: tab, name: '', cards: [], max_cards: Infinity, name_max: 30, text_max: 150, access: null };
  const isQuestion = shown.type === 'question';
  // A shared screen keeps the level it loaded with; a later refused write shows the general error.
  const isOwner = shown.access === 'owner';
  const isShared = shown.access === 'view' || shown.access === 'edit';
  const canWrite = shown.access !== 'view';
  const path = `decks/${encodeURIComponent(deckId)}`;
  const locked = shown.cards.length >= shown.max_cards && editing === null;
  const blanks = parts.length - 1;
  const length = isQuestion ? partsLength(parts) : text.length;
  const empty = isQuestion ? blanks === 0 && isBlank(parts[0]) : isBlank(text);
  // A deck that never loaded stays null; a write that succeeded meanwhile loads it again instead.
  const updateCards = change => setDeck(d => d && { ...d, cards: change(d.cards) });

  // Resolves to whether the write succeeded.
  const run = (action, request, onFail = () => {}) => {
    if (busy.current[action]) return Promise.resolve(true);
    busy.current[action] = true;
    setError(false);
    const write = (async () => {
      try {
        await request();
        if (deck === null && live.current) load();
        return true;
      } catch (e) {
        if (!live.current || await onAuthFailure(e)) return false;
        onFail();
        setError(true);
        if (e.response?.status === 409 && e.response.data?.detail === 'deck_full') load();
        return false;
      } finally {
        busy.current[action] = false;
      }
    })();
    pending.current.add(write);
    write.finally(() => pending.current.delete(write));
    return write;
  };

  // Back first lets every write of this screen settle (the tap itself blurs the name and starts a rename);
  // after a failed one the screen stays, with the error under the card field.
  const leave = async () => {
    const results = await Promise.all([...pending.current]);
    if (live.current && results.every(Boolean)) onBack();
  };

  const clearField = () => {
    setText('');
    setParts(['']);
    setAnswers(1);
    setEditing(null);
    setFieldKey(k => k + 1);
  };

  const submit = () => {
    if (empty) return;
    const body = isQuestion ? { parts, answers } : { parts: [text] };
    if (editing) {
      const id = editing;
      run('save', async () => {
        const res = await axios.put(`${path}/cards/${id}`, body);
        if (!live.current) return;
        updateCards(cards => cards.map(c => (c.id === id ? res.data : c)));
        clearField();
      });
    } else {
      run('add', async () => {
        const res = await axios.post(`${path}/cards`, { ...body, lang: currentLanguage() });
        if (!live.current) return;
        updateCards(cards => [res.data, ...cards]);
        clearField();
      });
    }
  };

  const edit = card => {
    setEditing(card.id);
    setText(isQuestion ? '' : card.parts[0]);
    setParts(isQuestion ? card.parts : ['']);
    setAnswers(card.answers ?? 1);
    setFieldKey(k => k + 1);
  };

  const removeCard = card => run('deleteCard', async () => {
    await axios.delete(`${path}/cards/${card.id}`);
    if (!live.current) return;
    updateCards(cards => cards.filter(c => c.id !== card.id));
    if (editing === card.id) clearField();
  });

  const rename = () => {
    if (isBlank(name)) {
      setName(shown.name);
      return;
    }
    if (name === shown.name) return;
    run('rename', async () => {
      const res = await axios.patch(path, { name });
      if (!live.current) return;
      setDeck(d => d && { ...d, name: res.data.name });
      setName(res.data.name);
    }, () => setName(shown.name));
  };

  const removeDeck = () => run('deleteDeck', async () => {
    await axios.delete(path);
    if (live.current) onBack();
  }, () => setConfirm(false));

  const fullHint = locked && (
    <p className="hint" id="full-hint">{t('deck.full', { count: shown.max_cards })}</p>
  );
  const addButton = locked
    ? <button type="button" className="btn btn-sm btn-disabled" aria-disabled="true" style={isQuestion ? undefined : { minHeight: 52 }}>{t('deck.add')}</button>
    : <button type="button" className="btn btn-dark btn-sm" style={isQuestion ? undefined : { minHeight: 52 }} onClick={submit}>{editing ? t('common.save') : t('deck.add')}</button>;
  const label = editing ? t('deck.editCard') : isQuestion ? t('deck.newQuestionCard') : t('deck.newAnswerCard');
  const count = <span className="count">{t('common.counter', { n: length, max: shown.text_max })}</span>;

  const answerField = (
    <div className="field">
      <label className="label" htmlFor="new-card"><span>{label}</span>{count}</label>
      <div className="row">
        <input id="new-card" className={locked ? 'input input-locked' : editing ? 'input input-focus' : 'input'}
          placeholder={editing ? undefined : t('deck.cardPlaceholder')} maxLength={shown.text_max} value={text} dir={textDir(text)} disabled={locked}
          aria-describedby={locked ? 'full-hint' : undefined} style={{ flex: 1 }}
          onChange={e => setText(e.target.value.substring(0, shown.text_max))} />
        {addButton}
      </div>
      {fullHint}
    </div>
  );

  const blankButton = locked
    ? <button type="button" className="btn btn-sm btn-disabled" aria-disabled="true"><PlusIcon size="18" />{t('deck.blank')}</button>
    : blanks >= MAX_BLANKS
      ? <button type="button" className="btn btn-sm btn-disabled" aria-disabled="true" aria-describedby="blank-hint"><PlusIcon size="18" />{t('deck.blank')}</button>
      : <button type="button" className="btn btn-sm" onClick={() => question.current?.insertBlank()}><PlusIcon size="18" />{t('deck.blank')}</button>;

  const questionField = (
    <div className="field">
      <span className="label" id="new-q"><span>{label}</span>{count}</span>
      <QuestionField key={`${fieldKey}-${locked}`} ref={question} initial={parts} maxLength={shown.text_max} locked={locked}
        editing={editing !== null} onChange={setParts} dir={textDir(parts.join(''))} aria-labelledby="new-q" aria-describedby={locked ? 'full-hint' : undefined} />
      <div className="row" style={{ alignItems: 'center' }}>
        {blankButton}
        <span style={{ flex: 1 }}></span>
        {addButton}
      </div>
      {!locked && blanks >= MAX_BLANKS && <p className="hint" id="blank-hint">{t('deck.maxBlanks')}</p>}
      {fullHint}
    </div>
  );

  const answerCount = isQuestion && !locked && blanks === 0 && (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="label" style={{ padding: 0, marginBottom: 6 }}>{t('deck.howManyAnswers')}</legend>
      <div className="seg" role="radiogroup" aria-label={t('deck.howManyAnswers')} style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        {[1, 2, 3].map(n => (
          <button key={n} type="button" className={answers === n ? 'seg-btn seg-on' : 'seg-btn'} role="radio"
            aria-checked={answers === n ? 'true' : 'false'} onClick={() => setAnswers(n)}>{n}</button>
        ))}
      </div>
      <p className="hint">{t('deck.answersHint')}</p>
    </fieldset>
  );

  const cardText = card => (isQuestion
    ? (
      <p className="card-text" dir={textDir(card.parts.join(''))}>
        {card.parts.map((part, i) => <React.Fragment key={i}>{i > 0 && <span className="blank"></span>}{part}</React.Fragment>)}
        <span className="card-meta" dir={screenDir()}>{answersMeta(card.answers)}</span>
      </p>
    )
    : <p className="card-text" dir={textDir(card.parts[0])}>{card.parts[0]}</p>);

  const sheetOpen = sharing && isOwner;
  const hidden = confirm || sheetOpen ? 'true' : undefined;
  return (
    <main className="screen">
      <header className="topbar" aria-hidden={hidden}>
        <a className="icon-btn" href="#back" onClick={onTap(leave)} aria-label={t('common.back')}><BackIcon /></a>
        <h1 className="topbar-title"><Bdi>{shown.name}</Bdi></h1>
        {isOwner && <a className="btn btn-sm" href="#share" onClick={onTap(onShare)}><ShareIcon />{t('deck.share')}</a>}
        <LanguageMenu />
      </header>
      <div className="body" aria-hidden={hidden} style={{ gap: 14, overflowY: 'auto' }}>
        {isOwner && (
          <div className="field">
            <label className="label" htmlFor="deck-name"><span>{t('deck.name')}</span><span className="count">{t('common.counter', { n: name.length, max: shown.name_max })}</span></label>
            <input id="deck-name" className="input" value={name} dir={textDir(name)} maxLength={shown.name_max}
              onChange={e => setName(e.target.value.substring(0, shown.name_max))} onBlur={rename} />
          </div>
        )}
        {isShared && (
          <>
            <div className="owner"><PersonIcon size="20" /><span>{t('deck.ownedBy')}</span><b><Bdi>{shown.owner}</Bdi></b><LevelTag level={shown.access} /></div>
            <p className="hint">{shown.access === 'view' ? t('deck.viewHint') : t('deck.editHint')}</p>
          </>
        )}
        {canWrite && (isQuestion ? questionField : answerField)}
        {error && <GeneralError />}
        {canWrite && answerCount}
        <h2 className="sec">{cardCount(shown.cards.length)}</h2>
        {shown.cards.length === 0
          ? canWrite && <p className="hint">{t('deck.empty')}</p>
          : (
            <ul className="list">
              {shown.cards.map(card => (
                <li key={card.id} className={isQuestion ? 'card-row card-row-q' : 'card-row'}
                  aria-current={editing === card.id ? 'true' : undefined}
                  style={editing === card.id ? { boxShadow: '0 0 0 4px var(--mint)' } : undefined}>
                  {cardText(card)}
                  {canWrite && <button type="button" className="icon-btn" aria-label={t('deck.editThisCard')} onClick={() => edit(card)}><PencilIcon /></button>}
                  {canWrite && <button type="button" className="icon-btn" aria-label={t('deck.deleteCard')} onClick={() => removeCard(card)}><TrashIcon /></button>}
                </li>
              ))}
            </ul>
          )}
        {editing && <button type="button" className="btn btn-block" onClick={clearField}>{t('deck.cancelEdit')}</button>}
        {isOwner && <div style={{ flex: 1 }}></div>}
        {isOwner && <button type="button" className="btn btn-danger btn-block" onClick={() => setConfirm(true)}>{t('deck.delete')}</button>}
      </div>
      {sheetOpen && <ShareSheet deckId={deckId} name={shown.name} onClose={onCloseShare} onAuthFailure={onAuthFailure} />}
      {confirm && (
        <>
          <div className="scrim"></div>
          <section className="sheet" role="dialog" aria-modal="true" aria-labelledby="del-deck-q">
            <span className="grab" aria-hidden="true"></span>
            <p id="del-deck-q" className="h-l" style={{ textAlign: 'center' }}>{t('deck.deleteConfirm')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
              <a className="btn btn-primary" href="#yes" onClick={onTap(removeDeck)}>{t('common.yes')}</a>
              <a className="btn" href="#no" onClick={onTap(() => setConfirm(false))}>{t('common.no')}</a>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
